import {
  DRAW_CLIP_MS, DRAW_INTRO_MS, DRAW_PER_TEAM_MS, DRAW_SLOT_CLIPS, drawDurationMs, drawFrame, revealedTeams,
  type DrawPhase, type DrawState,
} from '@brochure/shared'

/**
 * Due video, mai riavvolti a metà: `intro` (rovista, `DRAW_INTRO_MS`) e `turn`, le clip del turno una dopo l'altra
 * (`DRAW_SLOT_CLIPS`, `DRAW_PER_TEAM_MS`) in loop: finisce nella posa da cui riparte. Riusare e riavvolgere le singole
 * clip mostrava per un attimo il fotogramma rimasto dall'uso precedente.
 */
export type DrawVideo = 'intro' | 'turn'
/** `paused`: fermo sul pollice alzato dopo l'ultima coppia, invece di tornare all'urna. */
export type VideoCue = { video: DrawVideo; offsetMs: number; paused: boolean }

/** Fine della clip `approve` nel video del turno: l'ultimo fotogramma col pollice alzato. */
const THUMBS_UP_MS = DRAW_SLOT_CLIPS.lastIndexOf('approve') * DRAW_CLIP_MS + DRAW_CLIP_MS - 50

export function videoAt(elapsedMs: number, teamCount: number): VideoCue {
  if (elapsedMs < DRAW_INTRO_MS) return { video: 'intro', offsetMs: Math.max(0, elapsedMs), paused: false }
  const inTeams = elapsedMs - DRAW_INTRO_MS
  const team = Math.floor(inTeams / DRAW_PER_TEAM_MS)
  const inSlot = inTeams % DRAW_PER_TEAM_MS
  if (team >= teamCount || (team === teamCount - 1 && inSlot >= THUMBS_UP_MS)) return { video: 'turn', offsetMs: THUMBS_UP_MS, paused: true }
  return { video: 'turn', offsetMs: inSlot, paused: false }
}

export type DrawBubble = { key: 'intro' | 'pickFirst' | 'name' | 'withWhom' | 'approved' | 'outro'; name?: string }

export type DrawScene = {
  phase: DrawPhase
  /** Numero (da 1) della coppia in estrazione; 0 nell'introduzione. */
  teamNumber: number
  /** Nomi già usciti per la coppia in estrazione (null = ancora nell'urna). */
  names: [string | null, string | null]
  bubble: DrawBubble
  stamped: boolean
  /** Coppie convalidate, la più recente in cima. */
  pairs: { number: number; names: [string, string] }[]
  /** Nomi ancora nell'urna. */
  urn: string[]
  /** Il server ha già svelato una coppia che questa risposta non contiene: va riletta subito. */
  isBehind: boolean
  /** Ms al prossimo svelamento (o alla fine): quando rileggere il torneo. */
  nextRevealMs: number
}

/** I due nomi nell'ordine del sorteggio, quello del nome della squadra ("Nico/Fede"): Zambe pesca prima Nico. */
function namesOf(team: DrawState['teams'][number] | undefined): [string, string] | null {
  if (!team) return null
  const [a, b] = team.players
  const players = a && b && team.name === `${b.name}/${a.name}` ? [b, a, ...team.players.slice(2)] : team.players
  return [players[0]?.name ?? '', players.slice(1).map((p) => p.name).join(' / ')]
}

/** Tutto ciò che l'estrazione mostra a `elapsedMs` dall'inizio, dalle sole coppie già svelate dal server. */
export function drawScene(draw: DrawState, participants: readonly { name: string }[], elapsedMs: number): DrawScene {
  const { phase, team, shown } = drawFrame(elapsedMs, draw.teamCount)
  const current = namesOf(draw.teams[team])
  const names: DrawScene['names'] = [shown >= 1 ? current?.[0] ?? null : null, shown >= 2 ? current?.[1] ?? null : null]
  const validated = Math.min(phase === 'approved' ? team + 1 : Math.max(team, 0), draw.teams.length)
  const pairs = draw.teams.slice(0, validated).map((t, i) => ({ number: i + 1, names: namesOf(t)! })).reverse()
  const drawn = new Set([...draw.teams.slice(0, Math.max(team, 0)).flatMap((t) => t.players.map((p) => p.name)), ...names.filter((n): n is string => n !== null)])
  const bubble: DrawBubble =
    phase === 'intro' ? { key: 'intro' }
    : phase === 'outro' || phase === 'done' ? { key: 'outro' }
    : phase === 'approved' ? { key: 'approved' }
    : phase === 'reveal' ? { key: 'name', name: names[shown - 1] ?? undefined }
    : shown === 1 && names[0] ? { key: 'withWhom', name: names[0] }
    : { key: 'pickFirst' }
  const revealed = revealedTeams(elapsedMs, draw.teamCount)
  const nextReveal = revealed < draw.teamCount ? DRAW_INTRO_MS + revealed * DRAW_PER_TEAM_MS : drawDurationMs(draw.teamCount)
  return {
    phase,
    teamNumber: phase === 'intro' ? 0 : Math.min(team + 1, draw.teamCount),
    names,
    bubble,
    stamped: phase === 'approved',
    pairs,
    urn: participants.map((p) => p.name).filter((n) => !drawn.has(n)),
    isBehind: draw.teams.length < revealed,
    nextRevealMs: Math.max(0, nextReveal - elapsedMs),
  }
}
